// ─────────────────────────────────────────────────────────────────────────────
// theme.ts — THE MISSION CONTROL PALETTE, SAMPLED NOT CHOSEN.
//
// Owner ruling (in-terminal, this session): "The palette is in the HTML's :root
// — --bg:#050912, --panel:#0c1728, --teal:#36e6d4, --blue:#41a7ff,
// --violet:#a897ff, --amber:#ffd17c, --green:#8cffb7, --red:#ff6464,
// --radius:24px, Inter. Sample them, do not choose them."
//
// Every value below is transcribed from that declaration. Nothing here was
// picked by eye and nothing was "harmonised" against another palette.
//
// WHY THIS FILE EXISTS RATHER THAN REXY_CONSOLE:
//   REXY_CONSOLE (#0A0A1A / #00BFFF / gold) is the JARVIS homepage console and is
//   a DIFFERENT surface. The ruling of 2026-08-01 makes the design final on every
//   visual matter, so where the two disagree the design wins HERE and REXY_CONSOLE
//   is untouched THERE. Two surfaces, two palettes, neither bleeding into the
//   other — the last session's drift was applying a withdrawn ruling that said
//   live tokens beat the design. They do not.
//
// TYPE: the design names Inter. Standing constraint — NO WEBFONT, NO SELF-HOSTED
// BINARY (rexyConsole REXY_FONT, owner ruling 2026-07-24). So Inter leads a system
// stack: it renders where the box has it and degrades to the OS-wide humanist sans
// where it does not. No fetch is added and no dependency is introduced.
// ─────────────────────────────────────────────────────────────────────────────

export const MC = {
  // ── the :root block, transcribed byte-for-byte from the design HTML ──
  bg: '#050912',
  bg2: '#08101f',
  panel: '#0c1728',
  panel2: '#101d31',
  panel3: '#13243b',
  line: 'rgba(135,220,255,.22)',
  line2: 'rgba(255,255,255,.08)',
  text: '#eaf5ff',
  muted: '#8ea5bb',
  dim: '#62798f',
  teal: '#36e6d4',
  blue: '#41a7ff',
  violet: '#a897ff',
  amber: '#ffd17c',
  orange: '#ff8d3d',
  red: '#ff6464',
  green: '#8cffb7',
  cardShadow: '0 18px 60px rgba(0,0,0,.35), inset 0 0 0 1px rgba(255,255,255,.04)',
  radius: 24,

  // The ↳ attached-admin colour, sampled from the design's own rule.
  attach: '#67dfd1',

  // ── derived ONLY as alphas of the sampled accents above; no new colour ──
  tealSoft: 'rgba(54,230,212,0.10)',
  tealEdge: 'rgba(54,230,212,0.34)',
  blueSoft: 'rgba(65,167,255,0.10)',
  violetSoft: 'rgba(168,151,255,0.10)',
  amberSoft: 'rgba(255,209,124,0.10)',
  greenSoft: 'rgba(140,255,183,0.10)',
  redSoft: 'rgba(255,100,100,0.10)',
  brandTint: 'rgba(54,230,212,.06)',
  brandEdge: 'rgba(54,230,212,.18)',

  // ── kept for surfaces already written against them; each maps onto a
  //    sampled token above rather than introducing a value of its own ──
  panelEdge: 'rgba(135,220,255,.22)',
  panelEdgeSoft: 'rgba(255,255,255,.08)',
  topHighlight: 'rgba(255,255,255,0.045)',
  railPlate: '#050912',
  textMuted: '#8ea5bb',
  textDim: '#62798f',
  textFaint: 'rgba(98,121,143,0.55)',
  shadow: '0 18px 60px rgba(0,0,0,.35), inset 0 0 0 1px rgba(255,255,255,.04)',
  shadowSoft: '0 10px 26px rgba(0,0,0,0.42)',
} as const;

/** The body's two radial gradients over --bg, and the sidebar's own gradient —
 *  both transcribed from the design rather than approximated. */
export const MC_BODY_BG =
  'radial-gradient(1100px 620px at 12% -8%, rgba(65,167,255,.10), transparent 60%),' +
  'radial-gradient(900px 560px at 88% 4%, rgba(168,151,255,.09), transparent 62%),' +
  '#050912';
export const MC_RAIL_BG =
  'linear-gradient(180deg, rgba(8,18,32,.97), rgba(5,10,18,.98))';
/** Active pill: the design's teal→blue gradient with a teal glow. */
export const MC_PILL_ACTIVE = 'linear-gradient(135deg, #36e6d4, #41a7ff)';

/** Inter first, then the OS-wide stack. No webfont, no binary — see the note above. */
export const MC_FONT =
  'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/** The status-dot vocabulary. THREE STATES, THREE VOICES, NEVER SHARED:
 *  live = a source answered with traffic · dormant = a source answered zero
 *  (a MEASUREMENT, not a gap) · idle = nothing declared yet · failed = transport. */
export const DOT = {
  live: MC.green,
  dormant: MC.amber,
  idle: MC.dim,
  failed: MC.red,
} as const;
export type DotTone = keyof typeof DOT;
